import { useEffect, useRef, useState } from "react";
import { useApiWithAuth } from "./useAuth.js";

export type ApplicationStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "SUSPENDED";

export interface MyApplication {
  type: "particular" | "kennel";
  status: ApplicationStatus;
  /** Where to view this application's status. */
  statusPath: string;
}

interface UseMyApplicationsOptions {
  /** Skip fetching when false (e.g. for admins who can't have applications). */
  enabled?: boolean;
}

/**
 * Fetches the signed-in user's publisher applications (Particular + Kennel),
 * so callers can react to an in-progress candidatura — e.g. the navbar button
 * target and the "já tens uma candidatura" banner on the Join Chooser.
 * A 404 from an endpoint simply means "no application of that type".
 */
export function useMyApplications({
  enabled = true,
}: UseMyApplicationsOptions = {}) {
  const { apiCallWithAuth, isAuthenticated, isPermissionsLoading } =
    useApiWithAuth();
  const [applications, setApplications] = useState<MyApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !isAuthenticated || isPermissionsLoading) {
      if (!isAuthenticated && !isPermissionsLoading) setLoading(false);
      return;
    }
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      const results: MyApplication[] = [];
      try {
        const res = await apiCallWithAuth("/api/advertiser/me");
        if (res.ok) {
          const { data } = await res.json();
          if (data) {
            results.push({
              type: "particular",
              status: data.status,
              statusPath: "/meu-anuncio/status",
            });
          }
        }
      } catch {
        // ignore
      }
      try {
        const res = await apiCallWithAuth("/api/kennel/me");
        if (res.ok) {
          const { data } = await res.json();
          if (data) {
            results.push({
              type: "kennel",
              status: data.status,
              statusPath: "/candidatura-canil/estado",
            });
          }
        }
      } catch {
        // ignore
      }
      setApplications(results);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isAuthenticated, isPermissionsLoading]);

  const pendingApplication =
    applications.find((a) => a.status === "PENDING") ?? null;

  // An active application blocks starting a new candidatura of either type.
  const activeApplication =
    applications.find(
      (a) => a.status === "APPROVED" || a.status === "PENDING",
    ) ?? null;

  return {
    applications,
    loading,
    hasApplication: applications.length > 0,
    pendingApplication,
    activeApplication,
  };
}
